/**
 * Chargement différé des médias marqués `lazy-*` : <iframe>, <video> (et leurs <source>), arrière-plans.
 *
 * Extrait tel quel de l'ancien snippet `observer-CFH` (retiré) : même logique, mêmes attributs, mêmes événements.
 * Chargé en `defer` avant `theme.js` (`layout/theme.liquid`), sans variables globales.
 *
 * Les <img> n'utilisent plus ce mécanisme : elles restent en `loading="lazy"` natif (voir `snippets/lazy-replace.liquid`).
 *
 * Marquage du HTML :
 * - <iframe>, <video>, <source>, <img> : `src` → `lazy-src`, `srcset` → `lazy-srcset` ;
 * - <video> sans `src`, sources enfants : `lazy-source` sur le <video> et `lazy-src` sur chaque <source> ;
 * - arrière-plan : <div lazy-bg="url"></div> ;
 * - `data-lazy-keep` : l'élément est ignoré par l'observateur jusqu'à `removeLazyKeep`.
 *
 * Événements écoutés sur `document` :
 * - `reloadLazyLoad` : charge immédiatement tous les médias lazy ;
 * - `reactivateLazyObserver` : réarme l'observateur (nouveaux médias injectés, ex. re-rendu d'une section) ;
 * - `removeLazyKeep` : retire `data-lazy-keep` et charge les médias de `detail.lazyParentElement`.
 */
(() => {
	const LAZY_TARGETS = [
		'iframe[lazy-src]',
		'video[lazy-src]',
		'video[lazy-source]',
		'img[lazy-src]',
		'img[lazy-srcset]',
		'source[lazy-srcset]',
		'source[lazy-src]',
		'[lazy-bg]',
	];
	const LAZY_SELECTOR = LAZY_TARGETS.join(', ');
	const LAZY_PENDING_SELECTOR = LAZY_TARGETS.map((target) => `${target}:not([data-lazy-keep])`).join(', ');
	const LAZY_KEPT_SELECTOR = LAZY_TARGETS.map((target) => `${target}[data-lazy-keep]`).join(', ');

	let lazyObserver = null;

	const removeLazyLoad = (element) => {
		const src = element.getAttribute('lazy-src');
		const srcset = element.getAttribute('lazy-srcset');
		const background = element.getAttribute('lazy-bg');
		const hasLazySources = element.hasAttribute('lazy-source');

		if ((element.tagName === 'IFRAME' || element.tagName === 'VIDEO') && src) {
			element.setAttribute('src', src);
			element.removeAttribute('lazy-src');
		}

		if (element.tagName === 'VIDEO' && hasLazySources) {
			element.querySelectorAll('source[lazy-src]').forEach((source) => {
				const sourceSrc = source.getAttribute('lazy-src');
				if (sourceSrc) {
					source.setAttribute('src', sourceSrc);
					source.removeAttribute('lazy-src');
				}
			});
			element.removeAttribute('lazy-source');
			element.load();
		}

		if (element.tagName === 'IMG') {
			if (src) {
				element.setAttribute('src', src);
				element.removeAttribute('lazy-src');
			}
			if (srcset) {
				element.setAttribute('srcset', srcset);
				element.removeAttribute('lazy-srcset');
			}
		}

		if (element.tagName === 'SOURCE') {
			if (srcset) {
				element.setAttribute('srcset', srcset);
				element.removeAttribute('lazy-srcset');
			}
			if (src) {
				element.setAttribute('src', src);
				element.removeAttribute('lazy-src');
			}
		}

		if (background) {
			element.style.backgroundImage = `url(${background})`;
			element.removeAttribute('lazy-bg');
		}
	};

	const lazyLoadWithObserver = () => {
		lazyObserver?.disconnect();

		lazyObserver = new IntersectionObserver(
			(entries, observer) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						removeLazyLoad(entry.target);
						observer.unobserve(entry.target);
					}
				});
			},
			{ root: null, rootMargin: '300% 0px', threshold: 0 },
		);

		document.querySelectorAll(LAZY_PENDING_SELECTOR).forEach((element) => lazyObserver.observe(element));
	};

	const removeLazyKeep = (container) => {
		container?.querySelectorAll(LAZY_KEPT_SELECTOR).forEach((element) => {
			element.removeAttribute('data-lazy-keep');
			removeLazyLoad(element);
		});
	};

	lazyLoadWithObserver();

	// Filet de sécurité : détecte tout nouveau nœud lazy ajouté au DOM (facettes AJAX, pagination, changement de
	// variante, aperçu rapide, tiroir panier, recherche…) et l'enregistre auprès de l'IntersectionObserver, sans
	// avoir à émettre `reactivateLazyObserver` partout où du HTML est injecté.
	new MutationObserver((mutations) => {
		if (!lazyObserver) return;

		for (const mutation of mutations) {
			for (const node of mutation.addedNodes) {
				if (node.nodeType !== Node.ELEMENT_NODE) continue;

				if (node.matches?.(LAZY_PENDING_SELECTOR)) {
					lazyObserver.observe(node);
				}
				node.querySelectorAll?.(LAZY_PENDING_SELECTOR).forEach((element) => lazyObserver.observe(element));
			}
		}
	}).observe(document.body, { childList: true, subtree: true });

	// À la première interaction, l'observateur est reconstruit une fois pour reprendre les médias ajoutés depuis.
	['click', 'scroll', 'mouseover', 'keydown', 'touchmove', 'touchstart', 'wheel'].forEach((eventName) => {
		document.addEventListener(eventName, () => {
			const body = document.body;
			if (!body.getAttribute('lazyLoaded')) {
				lazyLoadWithObserver();
				body.setAttribute('lazyLoaded', '1');
			}
		});
	});

	document.addEventListener('reloadLazyLoad', () => {
		document.querySelectorAll(LAZY_SELECTOR).forEach(removeLazyLoad);
	});

	document.addEventListener('reactivateLazyObserver', () => {
		document.body.removeAttribute('lazyLoaded');
		lazyLoadWithObserver();
	});

	document.addEventListener('removeLazyKeep', (event) => {
		removeLazyKeep(event.detail?.lazyParentElement);
	});
})();
